/**
 * CardSettingsMenu — two separate small corner icons on each fleet card.
 * FT-436 revised UX (2026-08-08).
 *
 * Jonathan's required UX (replacing the previous combined ⋯ dropdown):
 *
 *   •  3-dot (⋯) icon  → opens the SetImageModal (pic-edit flow).
 *                         GATED: only skipper / fleet(admin) / super_admin
 *                         (useCanSetEntityImage). Crew see NOTHING.
 *
 *   •  Cog (gear) icon → calls onOpenSettings with the full entity, navigating
 *                         directly to THAT entity's settings.
 *                         GATED: only when `canOpenSettings` is true (same gate
 *                         the sidebar uses). Crew / read-only roles see nothing.
 *
 * Both icons sit at z-20 (above the z-0 full-card open-overlay), and each calls
 * stopPropagation so tapping an icon does NOT also trigger the open-vessel action.
 *
 * Crew edge case: if BOTH gates are false (neither canSetImage nor canOpenSettings),
 * nothing renders — the card stays clean for read-only roles.
 */

import React, { useState } from 'react';
import { Settings, MoreHorizontal } from 'lucide-react';
import { useCanSetEntityImage } from './useCanSetEntityImage';
import SetImageModal from './SetImageModal';

const CardSettingsMenu = ({
  entityKind,
  entityId,
  entityName,
  entity,             // full entity object (passed back to onOpenSettings)
  canOpenSettings,    // existing settings-access gate, computed by the parent
  onOpenSettings,     // (entity) => void — opens THAT entity's settings directly
  capturedBy,
}) => {
  const canSetImage = useCanSetEntityImage(entityKind, entityId);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // If the user can do NOTHING here, render nothing (no empty icons for crew).
  if (!canSetImage && !canOpenSettings) return null;

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <>
      {/* 3-dot (⋯) — pic-edit; only for skipper/admin/super_admin */}
      {canSetImage && (
        <button
          type="button"
          onClick={(e) => { stop(e); setImageModalOpen(true); }}
          className="p-1.5 rounded-full bg-white/90 backdrop-blur text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          aria-label="Set image"
          data-testid={`card-photo-btn-${entityId}`}
        >
          <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
        </button>
      )}

      {/* Gear — settings shortcut; only when canOpenSettings */}
      {canOpenSettings && (
        <button
          type="button"
          onClick={(e) => { stop(e); onOpenSettings && onOpenSettings(entity); }}
          className="p-1.5 rounded-full bg-white/90 backdrop-blur text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          aria-label="Open settings"
          data-testid={`card-menu-${entityId}`}
        >
          <Settings className="w-4 h-4" aria-hidden="true" />
        </button>
      )}

      <SetImageModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        entityKind={entityKind}
        entityId={entityId}
        entityName={entityName}
        capturedBy={capturedBy}
      />
    </>
  );
};

export default CardSettingsMenu;
